import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { durationColor } from '@/lib/badgeColors';
import { Plus, Sparkles, MoreVertical, Headphones, Package, Trash2, Eye, Edit, Zap } from 'lucide-react';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadProducts = () => {
    setLoading(true);
    api.get('/products').then(r => setProducts(r.data)).catch(() => toast.error('Failed to load products')).finally(() => setLoading(false));
  };

  useEffect(() => { loadProducts(); }, []);

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      loadProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const guides = products.filter(p => p.type === 'guide');
  const packages = products.filter(p => p.type === 'package');

  const statusColor = (s) => {
    if (s === 'published') return 'bg-green-500/10 text-green-600 border-green-200';
    if (s === 'archived') return 'bg-gray-500/10 text-gray-500 border-gray-200';
    return 'bg-yellow-500/10 text-yellow-600 border-yellow-200';
  };

  const ProductCard = ({ product }) => (
    <Card className="group hover:border-[#1A9DA5]/50 hover:shadow-md transition-all duration-300" data-testid={`product-card-${product.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {product.type === 'guide' ? <Headphones className="w-4 h-4 text-[#1A9DA5]" /> : <Package className="w-4 h-4 text-purple-500" />}
            <Badge variant="outline" className={statusColor(product.status)}>{product.status}</Badge>
            {product.ai_generated && <Zap className="w-3 h-3 text-[#1A9DA5]" />}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/admin/products/${product.id}`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/${product.type === 'guide' ? 'guides' : 'packages'}/${product.slug}`, '_blank')}><Eye className="w-4 h-4 mr-2" />Preview</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={() => deleteProduct(product.id)}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <h3 className="font-semibold text-base mb-1 font-['Outfit'] line-clamp-2 group-hover:text-[#1A9DA5] transition-colors">{product.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{product.short_description}</p>
        <div className="flex items-center justify-between">
          {product.discounted_price !== null && product.discounted_price !== undefined ? (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-['Outfit'] text-[#E8735A]">{parseFloat(product.discounted_price) === 0 ? 'Free' : `$${product.discounted_price}`}</span>
              <span className="text-sm text-muted-foreground line-through">${product.package_price || product.price}</span>
            </div>
          ) : (
            <span className="text-lg font-bold font-['Outfit']">${product.package_price || product.price}</span>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {product.duration_minutes && <span className={`px-1.5 py-0.5 rounded text-[11px] ${durationColor(product.duration_minutes).bg} ${durationColor(product.duration_minutes).text}`}>{product.duration_minutes}min</span>}
            {product.category && <Badge variant="secondary" className="text-xs">{product.category}</Badge>}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Products</h1>
          <p className="text-muted-foreground mt-1">{products.length} total products</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/products/new"><Button variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Manual</Button></Link>
          <Link to="/admin/products/generate"><Button size="sm" className="bg-[#E8735A] hover:bg-[#D4614A] text-white"><Sparkles className="w-4 h-4 mr-1" />AI Generate</Button></Link>
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({products.length})</TabsTrigger>
          <TabsTrigger value="guides" data-testid="tab-guides">Guides ({guides.length})</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Packages ({packages.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-4">
          {loading ? <p className="text-muted-foreground">Loading...</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="guides" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {guides.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </TabsContent>
        <TabsContent value="packages" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
